import { QueryClient } from '@tanstack/react-query'
import { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import { IssueT } from '@/routes/projectStages/issues/lib/types/issuesTypes.ts'
import { Issue, StageLocations } from '@/routes/projectStages/issue/types/issue.ts'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import { api } from '@/lib/api-client.ts'
import { usePrefetchStore, PrefetchState } from '@/store/usePrefetchStore.ts'
import { getSvgText, collectSvgUrlsFromLocations } from '@/lib/prefetch/svgCache'
import { pausePersist, resumePersist } from '@/lib/queryPersister.ts'
import { prefetchAttachments } from '@/lib/prefetch/prefetchAttachments'
import { getCachedAttachmentCount } from '@/lib/prefetch/offlineAttachmentCache'

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[Prefetch]', ...args)

/**
 * Extended issue type returned by the offline-bundle endpoint.
 * Includes first-page comments/events and their total counts.
 */
type BundleIssue = Issue & {
  offlineComments?: Record<string, unknown>[] | null
  offlineCommentsTotalItems?: number | null
  offlineEvents?: Record<string, unknown>[] | null
  offlineEventsTotalItems?: number | null
}

const COMMENTS_PAGE_SIZE = 10
const EVENTS_PAGE_SIZE = 10

export type StagePrefetchEntry = {
  stageId: string
  issueTypes: IssueTypeT[]
}

type PrefetchParams = {
  queryClient: QueryClient
  stages: StagePrefetchEntry[]
  signal: AbortSignal
  skipCacheCheck?: boolean
}

/**
 * Number of issues to seed per batch before yielding to the main thread.
 * Each issue triggers 3 setQueryData calls (issue + comments + events), so
 * a batch of 10 = ~30 synchronous cache operations (~5-10 ms). Keeping this
 * small ensures the browser gets enough idle frames to stay responsive.
 */
const SEED_BATCH_SIZE = 10

/**
 * Yield to the main thread so the browser can process user interactions,
 * paint frames, and run GC. Uses `requestIdleCallback` when available
 * (processes next batch only when truly idle) with a 200 ms ceiling so
 * prefetch still makes progress if the page is busy. Falls back to a
 * 50 ms timer (~3 frames at 60 fps) on platforms without rIC (WKWebView).
 */
function yieldToMain(): Promise<void> {
  return new Promise<void>(resolve => {
    const ric = (
      self as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number
      }
    ).requestIdleCallback
    if (typeof ric === 'function') {
      ric(() => resolve(), { timeout: 200 })
    } else {
      setTimeout(resolve, 50)
    }
  })
}

// --- Progress update throttling ---
// Zustand notifies ALL subscribers on every setState, and the progress UI
// subscribes with 9+ selectors. With 300+ progress increments per prefetch,
// this floods the main thread with equality checks and causes visible UI jank
// (unresponsive clicks, stuttering scroll). Throttling to ~7 updates/sec
// keeps the progress bar smooth without saturating the main thread.

const PROGRESS_FLUSH_MS = 150

let pendingProgress: Partial<PrefetchState> | null = null
let progressFlushTimer: ReturnType<typeof setTimeout> | null = null

function flushProgress(): void {
  if (progressFlushTimer !== null) {
    clearTimeout(progressFlushTimer)
    progressFlushTimer = null
  }
  if (pendingProgress) {
    usePrefetchStore.setState(pendingProgress)
    pendingProgress = null
  }
}

function scheduleProgressFlush(): void {
  if (progressFlushTimer !== null) return
  progressFlushTimer = setTimeout(flushProgress, PROGRESS_FLUSH_MS)
}

/**
 * Queue a throttled progress update. Accepts either a patch or an updater
 * function. Updater receives the current state merged with any pending
 * updates so increments compose correctly across calls within the window.
 */
function queueProgress(
  updater:
    | Partial<PrefetchState>
    | ((state: PrefetchState) => Partial<PrefetchState>),
): void {
  const patch =
    typeof updater === 'function'
      ? updater({
          ...usePrefetchStore.getState(),
          ...(pendingProgress ?? {}),
        } as PrefetchState)
      : updater
  pendingProgress = { ...(pendingProgress ?? {}), ...patch }
  scheduleProgressFlush()
}

/**
 * Apply an update immediately, flushing any pending throttled updates first
 * so ordering is preserved. Use for phase transitions and terminal states.
 */
function setProgressImmediate(partial: Partial<PrefetchState>): void {
  if (progressFlushTimer !== null) {
    clearTimeout(progressFlushTimer)
    progressFlushTimer = null
  }
  usePrefetchStore.setState({ ...(pendingProgress ?? {}), ...partial })
  pendingProgress = null
}

/**
 * Drop any pending progress updates without applying them. Call from abort
 * and cleanup paths where the store has been or will be reset.
 */
export function cancelPendingProgress(): void {
  if (progressFlushTimer !== null) {
    clearTimeout(progressFlushTimer)
    progressFlushTimer = null
  }
  pendingProgress = null
}

export function isCacheComplete(
  queryClient: QueryClient,
  stageId: string,
  issueTypes: IssueTypeT[],
): boolean {
  // Check stage users are cached
  const cachedUsers = queryClient.getQueryData([
    'currStageUsersQuery',
    { projectStageId: stageId },
  ])
  if (!cachedUsers) return false

  for (const issueType of issueTypes) {
    const cachedList = queryClient.getQueryData<IssueT[]>([
      'stageIssuesByIssueType',
      { projectStageId: String(stageId), issueTypeId: String(issueType.id) },
    ])
    if (!cachedList) return false
    for (const issue of cachedList) {
      if (
        !queryClient.getQueryData([
          'singleIssue',
          { id: String(issue.id) },
        ])
      ) {
        return false
      }
    }
  }
  return true
}

export type StageCacheStatus = 'complete' | 'partial' | 'none'

/**
 * Coarse three-state status for a stage's offline cache. Used by the
 * offline-cache settings page to show "Pobrane" / "Częściowo" / "Brak".
 *
 * "complete" means stage users + every issue type list + every list's issues
 * are all present (matches the existing `isCacheComplete` definition).
 * "partial" means at least one of those keys is present but the full set is
 * missing — i.e., a download was interrupted or only some issue types loaded.
 * "none" means no stage data of any kind is cached.
 *
 * Attachment completeness is intentionally NOT factored in: attachments are
 * best-effort (large originals are skipped, web has its own SW cache), and
 * including them would force the row to read Dexie just to compute a label.
 */
export function getStageCacheStatus(
  queryClient: QueryClient,
  stageId: string,
): StageCacheStatus {
  const usersCached = queryClient.getQueryData([
    'currStageUsersQuery',
    { projectStageId: stageId },
  ])
  const issueTypesCached = queryClient.getQueryData<IssueTypeT[]>([
    'currStageIssueTypes',
    { projectStageId: stageId },
  ])
  const lists = queryClient.getQueriesData<IssueT[]>({
    queryKey: ['stageIssuesByIssueType', { projectStageId: stageId }],
  })
  const locationsCached = queryClient.getQueryData([
    'stageLocations',
    { id: stageId },
  ])

  const hasAnyList = lists.some(([, data]) => data != null)
  const hasAny = !!usersCached || !!issueTypesCached || hasAnyList || !!locationsCached

  if (!hasAny) return 'none'

  if (
    issueTypesCached &&
    issueTypesCached.length > 0 &&
    isCacheComplete(queryClient, stageId, issueTypesCached)
  ) {
    return 'complete'
  }

  return 'partial'
}

function isAllCacheComplete(
  queryClient: QueryClient,
  stages: StagePrefetchEntry[],
): boolean {
  return stages.every(({ stageId, issueTypes }) =>
    isCacheComplete(queryClient, stageId, issueTypes),
  )
}

type BundlePageResult = {
  issues: BundleIssue[]
  totalItems: number
  hasNextPage: boolean
}

async function fetchIssuesPage(
  stageId: string,
  issueTypeId: string,
  page: number,
  signal: AbortSignal,
): Promise<BundlePageResult> {
  const response = await api.get(
    `/project_stages/${stageId}/issue_types/${issueTypeId}/issues/offline-bundle`,
    { params: { page }, signal },
  )
  return {
    issues: response.data['hydra:member'] ?? [],
    totalItems: response.data['hydra:totalItems'] ?? 0,
    hasNextPage: !!response.data['hydra:view']?.['hydra:next'],
  }
}

/**
 * Build a synthetic Hydra infinite-query page from pre-fetched data.
 * Mirrors the shape returned by `/issues/{id}/comments?page=1` or
 * `/issues/{id}/issue_events?page=1`.
 */
function buildHydraPage(
  members: Record<string, unknown>[],
  totalItems: number,
  issueId: string,
  resource: 'comments' | 'issue_events',
  pageSize: number,
) {
  const hasNextPage = totalItems > pageSize
  return {
    'hydra:member': members,
    'hydra:totalItems': totalItems,
    ...(hasNextPage
      ? { 'hydra:view': { 'hydra:next': `/issues/${issueId}/${resource}?page=2` } }
      : {}),
  }
}

/**
 * Extract offline comments/events from a bundle issue and seed
 * the infinite-query caches. Strips the offline fields from the
 * issue object to avoid storing them twice.
 */
function seedCommentsAndEvents(
  queryClient: QueryClient,
  bundleIssue: BundleIssue,
): Issue {
  const issueId = String(bundleIssue.id)

  // Only seed comments/events if not already cached — the user may have
  // loaded the full data via the issue detail page (richer than the bundle).
  const commentsKey = ['singleIssueComments', { id: issueId }]
  if (!queryClient.getQueryData(commentsKey) && bundleIssue.offlineComments && bundleIssue.offlineCommentsTotalItems != null) {
    const page = buildHydraPage(
      bundleIssue.offlineComments,
      bundleIssue.offlineCommentsTotalItems,
      issueId,
      'comments',
      COMMENTS_PAGE_SIZE,
    )
    queryClient.setQueryData(commentsKey, { pages: [page], pageParams: [1] })
  }

  const eventsKey = ['singleIssueEvents', { id: issueId }]
  if (!queryClient.getQueryData(eventsKey) && bundleIssue.offlineEvents && bundleIssue.offlineEventsTotalItems != null) {
    const page = buildHydraPage(
      bundleIssue.offlineEvents,
      bundleIssue.offlineEventsTotalItems,
      issueId,
      'issue_events',
      EVENTS_PAGE_SIZE,
    )
    queryClient.setQueryData(eventsKey, { pages: [page], pageParams: [1] })
  }

  // Strip offline fields to avoid storing them in the singleIssue cache
  const {
    offlineComments: _c,
    offlineCommentsTotalItems: _ct,
    offlineEvents: _e,
    offlineEventsTotalItems: _et,
    ...issue
  } = bundleIssue

  return issue as Issue
}

/**
 * Seeds individual issue caches (singleIssue + comments + events) for a page
 * of bundle issues. Returns stripped issues (without offline fields) for
 * accumulation into the list cache.
 *
 * Processes in batches of SEED_BATCH_SIZE with micro-task yields between
 * batches to allow WKWebView's GC to reclaim memory.
 */
async function seedIssueCaches(
  queryClient: QueryClient,
  bundleIssues: BundleIssue[],
  signal: AbortSignal,
): Promise<Issue[]> {
  const strippedIssues: Issue[] = []
  debug(`seedIssueCaches: seeding ${bundleIssues.length} issues`)

  for (let i = 0; i < bundleIssues.length; i += SEED_BATCH_SIZE) {
    if (signal.aborted) return strippedIssues

    const end = Math.min(i + SEED_BATCH_SIZE, bundleIssues.length)
    for (let j = i; j < end; j++) {
      const issue = seedCommentsAndEvents(queryClient, bundleIssues[j])
      strippedIssues.push(issue)

      // Only seed if no existing data. The route loader fetches the full
      // issue (issue:read group) which has more fields than the offline
      // bundle (issue:offline-bundle group). Overwriting a full response
      // with the bundle's subset causes crashes on the issue detail page.
      const issueKey = ['singleIssue', { id: String(issue.id) }]
      if (!queryClient.getQueryData(issueKey)) {
        queryClient.setQueryData(issueKey, issue)
      }
    }

    debug(`seedIssueCaches: batch ${i}-${end} done`)

    if (end < bundleIssues.length) {
      await yieldToMain()
    }
  }

  return strippedIssues
}

function countCachedIssues(queryClient: QueryClient): number {
  return queryClient
    .getQueriesData<Issue>({ queryKey: ['singleIssue'] })
    .filter(([, data]) => data != null).length
}

const LAST_COMPLETED_KEY = 'prefetch_lastCompletedAt'

function persistLastCompletedAt(timestamp: number): void {
  try {
    localStorage.setItem(LAST_COMPLETED_KEY, String(timestamp))
  } catch {
    // localStorage may be unavailable in some contexts
  }
}

export function getPersistedLastCompletedAt(): number | null {
  try {
    const stored = localStorage.getItem(LAST_COMPLETED_KEY)
    return stored ? Number(stored) : null
  } catch {
    return null
  }
}

export async function prefetchIssues({
  queryClient,
  stages,
  signal,
  skipCacheCheck = false,
}: PrefetchParams): Promise<void> {
  debug(`prefetchIssues called: ${stages.length} stages, skipCacheCheck=${skipCacheCheck}`)

  if (!skipCacheCheck && isAllCacheComplete(queryClient, stages)) {
    const now = Date.now()
    persistLastCompletedAt(now)
    setProgressImmediate({
      phase: 'done',
      completedIssues: countCachedIssues(queryClient),
      completedAttachments: await getCachedAttachmentCount(),
      lastCompletedAt: now,
    })
    return
  }

  const totalIssueTypes = stages.reduce(
    (sum, entry) => sum + entry.issueTypes.length,
    0,
  )

  // Single initial state setup — one render
  setProgressImmediate({
    phase: 'fetching-lists',
    isVisible: true,
    totalIssues: 0,
    completedIssues: 0,
    totalIssueTypes,
    completedIssueTypes: 0,
    totalStages: stages.length,
    completedStages: 0,
    totalSvgs: 0,
    completedSvgs: 0,
    totalAttachments: 0,
    completedAttachments: 0,
    currentIssueTypeName: '',
    errorCount: 0,
    lastCompletedAt: null,
    forceRefetchRequested: false,
  })

  if (signal.aborted) {
    setProgressImmediate({ phase: 'cancelled' })
    return
  }

  // Prefetch stage users for all stages (needed for recipients, related users).
  // Pause persistence — each setQueryData in the loop would otherwise trigger
  // full cache dehydration if prior session data was restored from IndexedDB.
  debug(`Starting stage users prefetch for ${stages.length} stages`)
  pausePersist()
  try {
    await prefetchStageUsers(queryClient, stages, signal)
  } finally {
    await resumePersist()
  }
  debug('Stage users prefetch done')

  if (signal.aborted) {
    setProgressImmediate({ phase: 'cancelled' })
    return
  }

  // Phase 1: Prefetch issues per stage.
  // Pause/resume persistence PER STAGE instead of globally. This bounds peak
  // memory to one stage's worth of issues instead of accumulating all stages
  // before a single massive flush — which caused WKWebView OOM on iOS.
  let accumulatedIssueCount = 0

  for (const { stageId, issueTypes } of stages) {
    if (signal.aborted) {
      setProgressImmediate({ phase: 'cancelled' })
      return
    }

    debug(`Processing stage=${stageId} with ${issueTypes.length} issue types`)
    pausePersist()

    try {
      for (const issueType of issueTypes) {
        if (signal.aborted) {
          setProgressImmediate({ phase: 'cancelled' })
          return
        }

        const issueTypeId = String(issueType.id)

        // Skip if this specific issue type is already cached
        if (!skipCacheCheck && isCacheComplete(queryClient, stageId, [issueType])) {
          queueProgress(state => ({
            completedIssueTypes: state.completedIssueTypes + 1,
          }))
          continue
        }

        // Update current issue type name for UI
        queueProgress({ currentIssueTypeName: issueType.name ?? '' })

        try {
          // Fetch page 1 to get totalItems and first batch of issues
          debug(`Fetching page 1 for stage=${stageId} issueType=${issueTypeId} (${issueType.name})`)
          const firstPage = await fetchIssuesPage(stageId, issueTypeId, 1, signal)
          debug(`Page 1: ${firstPage.issues.length} issues, totalItems=${firstPage.totalItems}, hasNext=${firstPage.hasNextPage}`)

          accumulatedIssueCount += firstPage.totalItems

          // Phase transition is immediate so the UI flips promptly;
          // totalIssues rides along in the same flush.
          setProgressImmediate({
            phase:
              usePrefetchStore.getState().phase === 'fetching-lists'
                ? 'fetching-details'
                : usePrefetchStore.getState().phase,
            totalIssues: accumulatedIssueCount,
          })

          // Yield after parsing the JSON response before the synchronous
          // setQueryData batch — lets the browser paint a frame.
          await yieldToMain()

          // Seed page 1 issues and collect stripped results
          const allStrippedIssues = await seedIssueCaches(queryClient, firstPage.issues, signal)
          debug(`Page 1 seeded, accumulated=${allStrippedIssues.length}`)

          queueProgress(state => ({
            completedIssues: state.completedIssues + firstPage.issues.length,
          }))

          // Fetch and seed remaining pages using hydra:view pagination
          let hasNextPage = firstPage.hasNextPage
          let currentPage = 2

          while (hasNextPage) {
            if (signal.aborted) {
              setProgressImmediate({ phase: 'cancelled' })
              return
            }

            debug(`Fetching page ${currentPage} for stage=${stageId} issueType=${issueTypeId}`)
            const pageResult = await fetchIssuesPage(stageId, issueTypeId, currentPage, signal)
            debug(`Page ${currentPage}: ${pageResult.issues.length} issues, hasNext=${pageResult.hasNextPage}`)
            await yieldToMain()
            const pageStripped = await seedIssueCaches(queryClient, pageResult.issues, signal)

            for (const issue of pageStripped) {
              allStrippedIssues.push(issue)
            }
            debug(`Page ${currentPage} seeded, accumulated=${allStrippedIssues.length}`)

            queueProgress(state => ({
              completedIssues: state.completedIssues + pageResult.issues.length,
            }))

            hasNextPage = pageResult.hasNextPage
            currentPage++
          }

          // Write list cache with all accumulated stripped issues for this issue type
          debug(`Writing list cache: ${allStrippedIssues.length} issues for stage=${stageId} issueType=${issueTypeId}`)
          queryClient.setQueryData(
            ['stageIssuesByIssueType', { projectStageId: stageId, issueTypeId }],
            allStrippedIssues,
          )

          queueProgress(state => ({
            completedIssueTypes: state.completedIssueTypes + 1,
          }))

          // Yield between issue types so the browser stays responsive
          await yieldToMain()
        } catch (error) {
          const responseBody = (error as Record<string, unknown>)?.response
            ? JSON.stringify((error as { response: { data: unknown } }).response.data).slice(0, 2000)
            : 'no response body'
          debug(`Error fetching issueType=${issueTypeId}: status=${(error as { status?: number }).status} body=${responseBody}`)
          if (signal.aborted) {
            setProgressImmediate({ phase: 'cancelled' })
            return
          }
          queueProgress(state => ({
            errorCount: state.errorCount + 1,
            completedIssueTypes: state.completedIssueTypes + 1,
          }))
        }
      }
    } finally {
      // Flush this stage's data to IndexedDB before moving to next stage.
      // Uses finally to ensure persistence is always resumed, even on abort/error.
      await resumePersist()
    }

    // Yield to let GC reclaim the previous stage's temporary objects
    await yieldToMain()

    queueProgress(state => ({
      completedStages: state.completedStages + 1,
    }))
  }

  if (signal.aborted) {
    setProgressImmediate({ phase: 'cancelled' })
    return
  }

  // Phase 2: Prefetch locations and SVG floor plans per stage.
  // Pause persistence — each SVG setQueryData would otherwise trigger
  // full cache dehydration (hundreds of issues), causing OOM.
  debug('Phase 1 complete. Starting Phase 2: locations + SVGs')
  pausePersist()
  try {
    await prefetchLocationsAndSvgs(queryClient, stages, signal)
    debug('Phase 2 complete')

    if (signal.aborted) {
      setProgressImmediate({ phase: 'cancelled' })
      return
    }

    // Phase 3: Attachment download for offline use.
    // Native: persisted to Capacitor Filesystem + Dexie index.
    // Web: fetch() triggers SW's StaleWhileRevalidate → cached in media-uploads-cache.
    //
    // We iterate stages so each attachment row gets the correct `stageId` in
    // Dexie — required for per-stage size aggregation and per-stage clear.
    // Without this, a multi-stage run would write every attachment with the
    // *first* stageId and per-stage stats would be wrong.
    debug('Starting Phase 3: attachments')
    setProgressImmediate({ phase: 'fetching-attachments' })
    try {
      for (const { stageId } of stages) {
        if (signal.aborted) {
          setProgressImmediate({ phase: 'cancelled' })
          return
        }
        await prefetchAttachments(queryClient, signal, { stageId })
      }
      debug('Phase 3 complete')
    } catch {
      // Non-critical — attachment caching failures don't block completion
      debug('Phase 3 failed (non-critical)')
      if (signal.aborted) {
        setProgressImmediate({ phase: 'cancelled' })
        return
      }
    }
  } finally {
    // Flush phases 2+3 to IndexedDB
    await resumePersist()
  }

  const now = Date.now()
  persistLastCompletedAt(now)
  const finalIssueCount = countCachedIssues(queryClient)
  const finalAttachmentCount = await getCachedAttachmentCount()
  debug(`DONE: ${finalIssueCount} issues, ${finalAttachmentCount} attachments cached`)
  setProgressImmediate({
    phase: 'done',
    completedIssues: finalIssueCount,
    completedAttachments: finalAttachmentCount,
    lastCompletedAt: now,
  })
}

async function fetchStageUsers(
  stageId: string,
  signal: AbortSignal,
): Promise<ProjectStagesUsersT | null> {
  try {
    const response = await api.get(
      `/project_stages/${stageId}/users`,
      { signal },
    )
    return (response.data['hydra:member'] ?? response.data) as ProjectStagesUsersT
  } catch {
    return null
  }
}

async function prefetchStageUsers(
  queryClient: QueryClient,
  stages: StagePrefetchEntry[],
  signal: AbortSignal,
): Promise<void> {
  for (const { stageId } of stages) {
    if (signal.aborted) return

    // Skip if already cached
    const cached = queryClient.getQueryData([
      'currStageUsersQuery',
      { projectStageId: stageId },
    ])
    if (cached) continue

    const users = await fetchStageUsers(stageId, signal)
    if (users) {
      queryClient.setQueryData(
        ['currStageUsersQuery', { projectStageId: stageId }],
        users,
      )
    }
  }
}

async function fetchStageLocations(
  stageId: string,
  signal: AbortSignal,
): Promise<StageLocations | null> {
  try {
    const response = await api.get(
      `/project_stages/${stageId}/locations`,
      { signal },
    )
    return (response.data['hydra:member'] ?? response.data) as StageLocations
  } catch {
    return null
  }
}

export async function prefetchLocationsAndSvgs(
  queryClient: QueryClient,
  stages: StagePrefetchEntry[],
  signal: AbortSignal,
): Promise<void> {
  // Collect all SVG URLs across all stages first, to set accurate totals
  const stageLocationsMap = new Map<string, StageLocations>()

  for (const { stageId } of stages) {
    if (signal.aborted) return

    const locations = await fetchStageLocations(stageId, signal)
    if (locations) {
      stageLocationsMap.set(stageId, locations)

      // Seed locations cache
      queryClient.setQueryData(
        ['stageLocations', { id: stageId }],
        locations,
      )
    }
  }

  // Collect all unique SVG URLs across all stages
  const allSvgUrls = new Set<string>()
  for (const locations of stageLocationsMap.values()) {
    for (const url of collectSvgUrlsFromLocations(locations)) {
      allSvgUrls.add(url)
    }
  }

  if (allSvgUrls.size === 0) return

  setProgressImmediate({ totalSvgs: allSvgUrls.size })

  // Prefetch each SVG
  for (const svgUrl of allSvgUrls) {
    if (signal.aborted) return

    try {
      await getSvgText(svgUrl, signal)
    } catch {
      // Non-critical — skip failed SVGs
    }

    queueProgress(state => ({
      completedSvgs: state.completedSvgs + 1,
    }))
  }
}
